import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ArrowRight, Repeat, Search, ChevronDown, Check } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { BRANDS, markers } from '../utils/data';
import PageHelpModal from '../components/PageHelpModal';

/**
 * Color conversion and distance calculation utilities
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

// RGB to XYZ conversion (D65 illuminant)
function rgbToXyz(rgb) {
  let r = rgb.r / 255;
  let g = rgb.g / 255;
  let b = rgb.b / 255;

  // Apply sRGB gamma correction
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

  // Convert to XYZ (using D65 illuminant matrix)
  const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750;
  const z = r * 0.0193339 + g * 0.1191920 + b * 0.9503041;

  return { x: x * 100, y: y * 100, z: z * 100 };
}

// XYZ to LAB conversion (D65 illuminant)
function xyzToLab(xyz) {
  // D65 standard illuminant
  const refX = 95.047;
  const refY = 100.000;
  const refZ = 108.883;

  let x = xyz.x / refX;
  let y = xyz.y / refY;
  let z = xyz.z / refZ;

  // Apply LAB transformation
  const delta = 6/29;
  const t0 = delta * delta * delta;
  const m = 1 / (3 * delta * delta);
  const c = 4/29;

  x = x > t0 ? Math.pow(x, 1/3) : m * x + c;
  y = y > t0 ? Math.pow(y, 1/3) : m * y + c;
  z = z > t0 ? Math.pow(z, 1/3) : m * z + c;

  const L = 116 * y - 16;
  const a = 500 * (x - y);
  const b = 200 * (y - z);

  return { L, a, b };
}

// Convert hex to LAB
function hexToLab(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const xyz = rgbToXyz(rgb);
  return xyzToLab(xyz);
}

/**
 * RGB Euclidean distance
 */
function colorDistanceRGB(hex1, hex2) {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);

  if (!rgb1 || !rgb2) return Infinity;

  const rDiff = rgb1.r - rgb2.r;
  const gDiff = rgb1.g - rgb2.g;
  const bDiff = rgb1.b - rgb2.b;

  return Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff);
}

/**
 * Delta E (CIE76) - simpler but less accurate
 * For better perception, we use this simplified version
 */
function colorDistanceDeltaE(hex1, hex2) {
  const lab1 = hexToLab(hex1);
  const lab2 = hexToLab(hex2);

  if (!lab1 || !lab2) return Infinity;

  const deltaL = lab1.L - lab2.L;
  const deltaA = lab1.a - lab2.a;
  const deltaB = lab1.b - lab2.b;

  return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
}

/**
 * Convert color distance to a match percentage
 * Uses different max distances based on algorithm
 */
function getMatchPercentage(distance, algorithm) {
  // Different max distances for different algorithms
  const MAX_DISTANCE = algorithm === 'rgb' ? 150 : 50;

  if (distance === 0) return 100;

  const percentage = Math.max(0, 100 - (distance / MAX_DISTANCE * 100));
  return Math.round(percentage);
}

function getTextColor(hex) {
  const clean = (hex || '').replace('#', '');
  if (clean.length < 6) return '#1e293b';
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1e293b' : '#ffffff';
}

function getScoreColor(pct) {
  if (pct >= 95) return 'text-emerald-500';
  if (pct >= 85) return 'text-emerald-600';
  if (pct >= 70) return 'text-amber-500';
  if (pct >= 50) return 'text-orange-500';
  return 'text-slate-400';
}

function ResultCard({ marker, matchPct, displayCode, brandLabel, isBest }) {
  const textColor = getTextColor(marker.hex);
  return (
    <div className={`rounded-2xl border shadow-sm p-3 flex items-center gap-3 ${isBest ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-100'}`}>
      <div className="w-16 h-16 rounded-xl shrink-0 shadow-sm relative" style={{ backgroundColor: marker.hex }}>
        <span
          className="absolute bottom-2 left-2 text-[11px] font-mono font-semibold leading-tight"
          style={{ color: textColor }}
        >
          {displayCode}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{marker.name || displayCode}</p>
        <p className="text-xs text-slate-400 truncate">{brandLabel}</p>
      </div>
      {matchPct != null && (
        <div className="text-right shrink-0">
          <p className={`text-sm font-bold ${getScoreColor(matchPct)}`}>{matchPct}%</p>
          <p className="text-[10px] text-slate-400">match</p>
        </div>
      )}
    </div>
  );
}

function CustomSelect({ value, onChange, options }) {
  const [open, setOpen] = React.useState(false);
  const ref = useRef(null);
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const current = options.find((o) => o === value);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      >
        <span className="truncate">{current || value}</span>
        <ChevronDown size={13} className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-50 animate-fadeIn max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full px-4 py-1.5 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${value === opt ? 'text-rose-pastel-600 font-semibold' : 'text-slate-700'}`}
            >
              <span className="truncate">{opt}</span>
              {value === opt && <Check size={12} className="text-rose-pastel-500 flex-shrink-0 ml-2" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BrandConverterPage() {
  const { t } = useI18n();

  const [inputBrand, setInputBrand] = useState('Ohuhu (Old Code)');
  const [outputBrand, setOutputBrand] = useState('Touch');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [convertedResults, setConvertedResults] = useState([]);
  const [algorithm, setAlgorithm] = useState('rgb'); // 'rgb' or 'deltaE'

  // Extended brands list with Ohuhu variations
  const availableBrands = useMemo(() => {
    const baseBrands = BRANDS.filter(b => b !== 'All' && b !== 'My Collection');
    const extendedBrands = [];

    baseBrands.forEach(brand => {
      if (brand === 'Ohuhu') {
        extendedBrands.push('Ohuhu (Old Code)', 'Ohuhu (New Code)');
      } else if (brand === 'Sanjoki') {
        extendedBrands.push('Sanjoki (Old Code)', 'Sanjoki (New Code)');
      } else {
        extendedBrands.push(brand);
      }
    });

    return extendedBrands;
  }, []);

  // Parse brand to get actual brand name and code type
  const parseBrand = (brandString) => {
    if (brandString === 'Ohuhu (Old Code)') return { brand: 'Ohuhu', codeType: 'old' };
    if (brandString === 'Ohuhu (New Code)') return { brand: 'Ohuhu', codeType: 'new' };
    if (brandString === 'Sanjoki (Old Code)') return { brand: 'Sanjoki', codeType: 'old' };
    if (brandString === 'Sanjoki (New Code)') return { brand: 'Sanjoki', codeType: 'new' };
    return { brand: brandString, codeType: null };
  };

  // Get markers for input brand based on search
  const inputBrandMarkers = useMemo(() => {
    const { brand, codeType } = parseBrand(inputBrand);
    let brandMarkers = markers.filter(m => m.brand === brand);

    // Filter markers with old/new code type (Ohuhu, Sanjoki, etc.)
    if (codeType) {
      brandMarkers = brandMarkers.filter(m => {
        if (codeType === 'old') return m.code_old && m.code_old !== '-';
        return m.code_new && m.code_new !== '-';
      });
    }

    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return brandMarkers.filter(m => {
      const matchesName = m.name?.toLowerCase().includes(query);

      // For brands with a specific code type, only search in that code
      if (codeType) {
        if (codeType === 'old') return matchesName || m.code_old?.toLowerCase().includes(query);
        return matchesName || m.code_new?.toLowerCase().includes(query);
      }

      const matchesCode = m.code?.toLowerCase().includes(query);
      const matchesCodeOld = m.code_old?.toLowerCase().includes(query);
      const matchesCodeNew = m.code_new?.toLowerCase().includes(query);

      return matchesName || matchesCode || matchesCodeOld || matchesCodeNew;
    });
  }, [inputBrand, searchQuery]);

  // Get display code for a marker based on brand selection
  const getDisplayCode = (marker, brandString) => {
    const { codeType } = parseBrand(brandString);

    if (codeType === 'old') return marker.code_old || '-';
    if (codeType === 'new') return marker.code_new || '-';
    // Brands with old/new but no specific type selected (shouldn't normally reach here)
    if (marker.code === undefined && (marker.code_old || marker.code_new)) {
      return `${marker.code_old || '-'} / ${marker.code_new || '-'}`;
    }
    return marker.code || '-';
  };

  // Handle marker selection and conversion
  const handleMarkerSelect = async (marker) => {
    setSelectedMarker(marker);
    setIsConverting(true);
    setConvertedResults([]);
    setSearchQuery('');

    // Simulate loading animation
    await new Promise(resolve => setTimeout(resolve, 600));

    const { brand: inputBrandName, codeType: inputCodeType } = parseBrand(inputBrand);
    const { brand: outputBrandName, codeType: outputCodeType } = parseBrand(outputBrand);

    // Special case: Old ↔ New direct mapping (same marker, different code — Ohuhu, Sanjoki)
    if (inputBrandName === outputBrandName &&
        inputCodeType && outputCodeType && inputCodeType !== outputCodeType) {
      // Direct mapping exists (same marker, different code)
      setConvertedResults([{
        ...marker,
        matchType: 'direct',
        distance: 0
      }]);
      setIsConverting(false);
      return;
    }

    // General case: Find similar colors by hex
    let outputMarkers = markers.filter(m => m.brand === outputBrandName);

    // Filter output markers by code type if specified (Ohuhu, Sanjoki, etc.)
    if (outputCodeType) {
      outputMarkers = outputMarkers.filter(m => {
        if (outputCodeType === 'old') return m.code_old && m.code_old !== '-';
        return m.code_new && m.code_new !== '-';
      });
    }

    // Calculate distances and sort by similarity
    const colorDistanceFn = algorithm === 'rgb' ? colorDistanceRGB : colorDistanceDeltaE;

    const results = outputMarkers
      .map(m => ({
        ...m,
        matchType: 'approximate',
        distance: colorDistanceFn(marker.hex, m.hex)
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 10); // Top 10 matches

    setConvertedResults(results);
    setIsConverting(false);
  };

  // Reset when brands change
  const handleBrandChange = (type, brand) => {
    if (type === 'input') {
      setInputBrand(brand);
    } else {
      setOutputBrand(brand);
    }
    setSelectedMarker(null);
    setConvertedResults([]);
    setSearchQuery('');
  };

  // Re-calculate results when algorithm changes
  useEffect(() => {
    if (selectedMarker && convertedResults.length > 0) {
      handleMarkerSelect(selectedMarker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithm]);

  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-blue-400 to-cyan-500">
          {t('brandConverter.title')} <Repeat className="inline-block w-8 h-8 pb-1 text-cyan-500" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          {t('brandConverter.subtitle')}
        </p>
        <PageHelpModal
          title={t('brandConverter.help.title')}
          tips={[
            { text: t('brandConverter.help.tip1') },
            { text: t('brandConverter.help.tip2') },
            { text: t('brandConverter.help.tip3') },
            { text: t('brandConverter.help.tip4') },
          ]}
          trigger={
            <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
              {t('common.needHelp')}
            </p>
          }
        />
      </div>

      {/* Brand Selection */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input Brand */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            {t('brandConverter.inputBrand')}
          </label>
          <CustomSelect value={inputBrand} onChange={(v) => handleBrandChange('input', v)} options={availableBrands} />
        </div>

        {/* Output Brand */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            {t('brandConverter.outputBrand')}
          </label>
          <CustomSelect value={outputBrand} onChange={(v) => handleBrandChange('output', v)} options={availableBrands} />
        </div>
      </div>

      {/* Marker Search & Selection */}
      <div className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              {t('brandConverter.selectMarker')}
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('brandConverter.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Algorithm Toggle */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              {t('brandConverter.algorithm')}
            </label>
            <div className="flex items-center gap-3 h-[50px]">
              {/* RGB Label */}
              <span className={`text-sm font-semibold transition-all duration-300 ${
                algorithm === 'rgb'
                  ? 'text-blue-600 scale-110'
                  : 'text-slate-400 scale-100'
              }`}>
                RGB
              </span>

              {/* Toggle Track */}
              <div
                className={`relative w-14 h-7 rounded-full cursor-pointer transition-all duration-300 ${
                  algorithm === 'rgb'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 shadow-md shadow-blue-200'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 shadow-md shadow-purple-200'
                }`}
                onClick={() => setAlgorithm(algorithm === 'rgb' ? 'deltaE' : 'rgb')}
              >
                {/* Sliding Circle */}
                <div
                  className={`absolute top-0.5 bg-white rounded-full shadow-lg transition-all duration-300 ease-in-out ${
                    algorithm === 'deltaE'
                      ? 'left-[30px] w-6 h-6 scale-110'
                      : 'left-0.5 w-6 h-6 scale-110'
                  }`}
                  style={{
                    transition: 'left 0.3s ease-in-out, width 0.3s ease-in-out, transform 0.3s ease-in-out'
                  }}
                />
              </div>

              {/* Delta E Label */}
              <span className={`text-sm font-semibold transition-all duration-300 ${
                algorithm === 'deltaE'
                  ? 'text-purple-600 scale-110'
                  : 'text-slate-400 scale-100'
              }`}>
                Delta E
              </span>
            </div>
          </div>
        </div>

        {/* Marker List - Only show when searching */}
        {searchQuery.trim() && (
          <div className="max-h-64 overflow-y-auto space-y-2 rounded-2xl border border-slate-200 p-4 bg-white shadow-lg">
            {inputBrandMarkers.length === 0 ? (
              <p className="text-center text-slate-500 py-4 text-sm">
                {t('brandConverter.noMarkersFound')}
              </p>
            ) : (
              inputBrandMarkers.map((marker, idx) => (
                <button
                  key={`${marker.brand}-${marker.code || marker.code_old || marker.code_new || idx}`}
                  onClick={() => handleMarkerSelect(marker)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-lg border-2 border-white shadow-md flex-shrink-0"
                    style={{ backgroundColor: marker.hex }}
                  />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-slate-800 text-sm">{marker.name}</p>
                    <p className="text-xs text-slate-500">
                      {getDisplayCode(marker, inputBrand)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Conversion Results */}
      {selectedMarker && (
        <div className="space-y-4">
          {isConverting ? (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
              <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4" />
              <p className="text-slate-600 font-medium">{t('brandConverter.converting')}</p>
            </div>
          ) : convertedResults.length === 0 ? (
            <div className="text-center text-slate-500 py-16 rounded-2xl bg-slate-50 border border-slate-200">
              {t('brandConverter.noResults')}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Source marker */}
              <ResultCard
                marker={selectedMarker}
                matchPct={null}
                displayCode={getDisplayCode(selectedMarker, inputBrand)}
                brandLabel={parseBrand(inputBrand).brand}
              />

              {/* Down arrow */}
              <div className="flex justify-center">
                <ArrowRight size={16} className="text-slate-300 rotate-90" />
              </div>

              {/* All matches */}
              {convertedResults.map((result, idx) => (
                <ResultCard
                  key={`${result.brand}-${result.code || result.code_old || result.code_new || idx}`}
                  marker={result}
                  matchPct={getMatchPercentage(result.distance, algorithm)}
                  displayCode={getDisplayCode(result, outputBrand)}
                  brandLabel={parseBrand(outputBrand).brand}
                  isBest={idx === 0}
                />
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
